import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAllSimulationsQuery,
  useGetAdminOrganizationsQuery,
  useLazyGetUserSimulationScoresQuery,
  AllSimulationItem,
  UserSimulationScore,
} from "../../app/services/meseeknaApiV2"
import { FiSearch, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi"
import { HiOutlineOfficeBuilding, HiOutlineUserCircle } from "react-icons/hi"
import { MdOutlinePlayCircle, MdOutlineCheckCircle, MdOutlinePending, MdOutlineInsights } from "react-icons/md"
import { BiTime, BiBarChartAlt2 } from "react-icons/bi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronLeftIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronLeft {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronRightIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronRight {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineUserCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const PlayIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlinePlayCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const PendingIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlinePending {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TimeIcon = (props: { size?: number; className?: string }): JSX.Element => <BiTime {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const InsightsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineInsights {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChartIcon = (props: { size?: number; className?: string }): JSX.Element => <BiBarChartAlt2 {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CloseIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />

const ITEMS_PER_PAGE = 25

const getScoreColor = (score: number): string => {
  if (score >= 80) return "bg-green-100 text-green-800"
  if (score >= 60) return "bg-blue-100 text-blue-800"
  if (score >= 40) return "bg-yellow-100 text-yellow-800"
  return "bg-red-100 text-red-800"
}

const StatusBadge: React.FC<{ status: AllSimulationItem["status"] }> = ({ status }) => {
  switch (status) {
    case "completed":
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
          <CheckIcon size={12} />
          Completed
        </span>
      )
    case "in_progress":
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
          <PlayIcon size={12} />
          In Progress
        </span>
      )
    case "not_started":
    default:
      return (
        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
          <PendingIcon size={12} />
          Not Started
        </span>
      )
  }
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "N/A"
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Score Modal Component
const ScoresModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  isLoading: boolean
  scores: UserSimulationScore[]
  userName: string
  testTitle: string
  dateCompleted: string | null
}> = ({ isOpen, onClose, isLoading, scores, userName, testTitle, dateCompleted }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Measure Scores</h3>
            <p className="text-sm text-gray-500">
              {userName} - {testTitle}
              {dateCompleted && <span className="ml-2">(Completed: {formatDate(dateCompleted)})</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <CloseIcon size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="h-10 w-10 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                  <div className="h-8 w-16 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : scores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <InsightsIcon size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="font-medium">No Scores Available</p>
              <p className="text-sm">This simulation has no measure scores recorded yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {scores.map((score) => (
                <div
                  key={score.measureId}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <ChartIcon size={20} className="text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{score.measureName}</p>
                    {score.measureCode && (
                      <p className="text-xs text-gray-400 font-mono">{score.measureCode}</p>
                    )}
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg font-bold ${getScoreColor(score.score)}`}>
                    {score.score.toFixed(1)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with stats */}
        {!isLoading && scores.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {scores.length} measures scored
              </span>
              <div className="flex items-center gap-4">
                <span className="text-gray-600">
                  Average: <strong className="text-gray-900">
                    {(scores.reduce((sum, s) => sum + s.score, 0) / scores.length).toFixed(1)}
                  </strong>
                </span>
                <span className="text-gray-600">
                  Range: <strong className="text-gray-900">
                    {Math.min(...scores.map(s => s.score)).toFixed(1)} - {Math.max(...scores.map(s => s.score)).toFixed(1)}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const AllSimulationsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "completed" | "not_started">("all")
  const [filterOrg, setFilterOrg] = useState<number | undefined>(undefined)
  const [currentPage, setCurrentPage] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedSimulation, setSelectedSimulation] = useState<AllSimulationItem | null>(null)

  // Lazy query for fetching scores
  const [fetchScores, { data: scoresData, isLoading: isLoadingScores }] = useLazyGetUserSimulationScoresQuery()

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(0) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data: orgsData } = useGetAdminOrganizationsQuery()
  const { data, isLoading, isFetching } = useGetAllSimulationsQuery({
    limit: ITEMS_PER_PAGE,
    offset: currentPage * ITEMS_PER_PAGE,
    status: filterStatus,
    organizationId: filterOrg,
    search: debouncedSearch || undefined,
  })

  const organizations = orgsData?.data || []
  const simulations = data?.data?.simulations || []
  const total = data?.data?.total || 0
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  const stats = useMemo(() => {
    return {
      total: total,
      showing: simulations.length,
      page: currentPage + 1,
      totalPages: totalPages || 1,
    }
  }, [total, simulations.length, currentPage, totalPages])

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleStatusFilter = (status: typeof filterStatus) => {
    setFilterStatus(status)
    setCurrentPage(0)
  }

  const handleOrgFilter = (orgId: number | undefined) => {
    setFilterOrg(orgId)
    setCurrentPage(0)
  }

  const handleViewScores = async (sim: AllSimulationItem) => {
    setSelectedSimulation(sim)
    fetchScores(sim.id)
  }

  const handleCloseScores = () => {
    setSelectedSimulation(null)
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <PlayIcon size={24} className="text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    All Simulations
                  </h1>
                  <p className="text-gray-500">
                    View all simulations across all organizations - click on completed simulations to view scores
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by user email, name, or simulation title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <select
                    value={filterStatus}
                    onChange={(e) => handleStatusFilter(e.target.value as typeof filterStatus)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="not_started">Not Started</option>
                    <option value="active">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Organization Filter */}
                <div className="flex items-center gap-2">
                  <BuildingIcon size={18} className="text-gray-400" />
                  <select
                    value={filterOrg || ""}
                    onChange={(e) => handleOrgFilter(e.target.value ? parseInt(e.target.value) : undefined)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.organizationName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900">{stats.showing}</span> of{" "}
                <span className="font-medium text-gray-900">{stats.total}</span> simulations
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  Page {stats.page} of {stats.totalPages}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon size={16} />
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Simulations Table */}
            {isLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="animate-pulse">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </div>
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="p-4 border-b border-gray-200">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="h-6 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : simulations.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <PlayIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Simulations Found
                </h3>
                <p className="text-gray-500">
                  {searchQuery || filterOrg || filterStatus !== "all"
                    ? "No simulations match your filter criteria."
                    : "There are no simulations in the system yet."}
                </p>
              </div>
            ) : (
              <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${isFetching ? "opacity-60" : ""}`}>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-600">
                  <div className="col-span-3">User</div>
                  <div className="col-span-2">Organization</div>
                  <div className="col-span-3">Simulation</div>
                  <div className="col-span-2">Dates</div>
                  <div className="col-span-2">Status / Scores</div>
                </div>

                {/* Table Body */}
                {simulations.map((sim) => (
                  <div
                    key={sim.id}
                    className={`grid grid-cols-12 gap-4 p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors items-center ${
                      sim.status === "completed" ? "cursor-pointer" : ""
                    }`}
                    onClick={() => sim.status === "completed" && handleViewScores(sim)}
                  >
                    {/* User */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <UserIcon size={20} className="text-indigo-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {sim.userFirstName && sim.userLastName
                              ? `${sim.userFirstName} ${sim.userLastName}`
                              : "Unknown User"}
                          </p>
                          <p className="text-sm text-gray-500 truncate">{sim.userEmail}</p>
                        </div>
                      </div>
                    </div>

                    {/* Organization */}
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <BuildingIcon size={16} className="text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{sim.organizationName}</span>
                      </div>
                    </div>

                    {/* Simulation */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-3">
                        {sim.testImgUrl ? (
                          <img
                            src={sim.testImgUrl}
                            alt={sim.testTitle}
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <PlayIcon size={20} className="text-gray-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{sim.testTitle}</p>
                          {sim.testShortName && (
                            <p className="text-sm text-gray-500 truncate">{sim.testShortName}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="col-span-2">
                      <div className="text-sm">
                        <div className="flex items-center gap-1 text-gray-600">
                          <TimeIcon size={12} />
                          <span className="truncate">Assigned: {formatDate(sim.dateAssigned)}</span>
                        </div>
                        {sim.dateUserStart && (
                          <div className="text-gray-500 truncate">
                            Started: {formatDate(sim.dateUserStart)}
                          </div>
                        )}
                        {sim.dateUserEnd && (
                          <div className="text-green-600 truncate">
                            Ended: {formatDate(sim.dateUserEnd)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status & Scores Button */}
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={sim.status} />
                        {sim.status === "completed" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewScores(sim)
                            }}
                            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-teal-100 text-teal-700 hover:bg-teal-200 transition-colors"
                          >
                            <InsightsIcon size={12} />
                            Scores
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Footer */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ChevronLeftIcon size={16} />
                  Previous
                </button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number
                    if (totalPages <= 5) {
                      pageNum = i
                    } else if (currentPage < 3) {
                      pageNum = i
                    } else if (currentPage > totalPages - 4) {
                      pageNum = totalPages - 5 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium ${
                          currentPage === pageNum
                            ? "bg-indigo-600 text-white"
                            : "border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    )
                  })}
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages - 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Next
                  <ChevronRightIcon size={16} />
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Scores Modal */}
      <ScoresModal
        isOpen={selectedSimulation !== null}
        onClose={handleCloseScores}
        isLoading={isLoadingScores}
        scores={scoresData?.data?.measureScores || []}
        userName={
          selectedSimulation
            ? selectedSimulation.userFirstName && selectedSimulation.userLastName
              ? `${selectedSimulation.userFirstName} ${selectedSimulation.userLastName}`
              : selectedSimulation.userEmail
            : ""
        }
        testTitle={selectedSimulation?.testTitle || ""}
        dateCompleted={selectedSimulation?.dateUserEnd || null}
      />
    </div>
  )
}

export default AllSimulationsPage
